// Which translation files a page needs, from the manifest that
// scripts/build-i18n-json.mjs writes at prebuild (and before dev).
//
// A page needs "core" (layout chrome and widely used keys) plus, when its
// component tree renders anything else, its route file "r-<set>" with
// exactly the keys found by the static scan (scripts/i18n-scan.mjs). The
// first page of a visit loads both as one combined file, "c-<set>" (one
// request); client navigations after that fetch only the next "r-" file.
// A key the scan missed resolves through its namespace file,
// "ns-<prefix>", fetched on demand by lib/locale-context.tsx.
import type { Locale } from "../i18n-config";
import ASSETS from "@/generated/i18n-assets.json";

interface Assets {
  // "core" or a route key set -> content hash (all three locales; a set's
  // hash names both its "r-" and "c-" files)
  files: Record<string, string>;
  // content hash of every "ns-" file
  ns: string;
  // [route pattern, route key set or null], most specific first
  routes: [string, string | null][];
}

const assets = ASSETS as unknown as Assets;

const CORE = "core";

type Matcher = { segs: string[]; id: string | null };

let compiled: Matcher[] | null = null;
function matchers(): Matcher[] {
  if (!compiled) {
    compiled = (assets.routes || []).map(([pattern, id]) => ({ segs: pattern.split("/").filter(Boolean), id }));
  }
  return compiled;
}

function matches(segs: string[], parts: string[]): boolean {
  for (let i = 0; i < segs.length; i++) {
    const s = segs[i];
    if (s.startsWith("[[...")) return true;
    if (s.startsWith("[...")) return parts.length > i;
    if (i >= parts.length) return false;
    if (s.startsWith("[")) continue;
    if (s !== parts[i]) return false;
  }
  return segs.length === parts.length;
}

/** The route key set for a URL path ("/az/alerts/12"), or null when the
 *  page needs only core (or matches no known route: not-found). */
export function routeSetFor(pathname: string): string | null {
  const parts = pathname.split("/").filter(Boolean);
  if (parts[0] === "az" || parts[0] === "ru" || parts[0] === "en") parts.shift();
  const decoded = parts.map((p) => {
    try {
      return decodeURIComponent(p);
    } catch {
      return p;
    }
  });
  for (const m of matchers()) if (matches(m.segs, decoded)) return m.id;
  return null;
}

/** The file to load for a route key set (routeSetFor): the combined file
 *  until core is in for the locale, then the set's own file; core alone
 *  for a page without keys of its own. */
export function fileFor(set: string | null, haveCore: boolean): string {
  if (!set) return CORE;
  return `${haveCore ? "r" : "c"}-${set}`;
}

/** The files a combined "c-" file stands for (core and its "r-" file),
 *  or null for any other file. */
export function partsOf(id: string): string[] | null {
  return id.startsWith("c-") ? [CORE, `r-${id.slice(2)}`] : null;
}

/** Public URL of a file for a locale, or undefined when the manifest has
 *  no such file (no build-i18n-json run yet). */
export function fileUrl(id: string, locale: Locale): string | undefined {
  const h = id === CORE ? assets.files?.core : id.startsWith("ns-") ? assets.ns : assets.files?.[id.slice(2)];
  return h ? `/i18n/${locale}/${id}.${h}.json` : undefined;
}

/** The on-demand file holding every key of a namespace. */
export function namespaceFile(ns: string): string {
  return `ns-${ns}`;
}
